import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/utils'

interface FieldCtx {
  id: string
  describedBy?: string
  invalid: boolean
}

/** Read by controls that are not an Input/Select/Textarea (SlotPicker) to
 *  take the label's id. */
export const FieldContext = createContext<FieldCtx | null>(null)

export interface FieldProps {
  label: ReactNode
  /** One line of help under the control. */
  hint?: ReactNode
  /** Replaces the hint, in the failure colour. */
  error?: ReactNode
  required?: boolean
  /** Something to the right of the label: a counter, a preset button. */
  aside?: ReactNode
  children: ReactNode
  className?: string
}

/** The one definition of a labelled control. The Input, Select and Textarea
 *  below pick up its id, hint and error by themselves. */
export function Field({ label, hint, error, required, aside, children, className }: FieldProps) {
  const id = useId()
  const noteId = `${id}-note`
  const note = error ?? hint
  return (
    <FieldContext.Provider value={{ id, describedBy: note ? noteId : undefined, invalid: !!error }}>
      <div className={cn('min-w-0 space-y-1.5', className)}>
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={id} className="text-meta font-medium text-foreground">
            {label}
            {required && (
              <span className="ml-0.5 text-status-failed" aria-hidden>
                *
              </span>
            )}
          </label>
          {aside && <div className="text-meta text-muted-foreground">{aside}</div>}
        </div>
        {children}
        {note && (
          <p id={noteId} className={cn('text-meta', error ? 'text-status-failed' : 'text-muted-foreground')}>
            {note}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
}

export interface FieldGroupProps {
  /** A heading over the group; leave out for a plain grid. */
  title?: ReactNode
  description?: ReactNode
  /** Two columns from the sm breakpoint up. */
  columns?: 1 | 2
  children: ReactNode
  className?: string
}

export function FieldGroup({ title, description, columns = 1, children, className }: FieldGroupProps) {
  return (
    <fieldset className={cn('min-w-0 space-y-3', className)}>
      {title && <legend className="text-section font-semibold">{title}</legend>}
      {description && <p className="-mt-1 text-meta text-muted-foreground">{description}</p>}
      <div className={cn('grid gap-3', columns === 2 && 'sm:grid-cols-2')}>{children}</div>
    </fieldset>
  )
}

export const controlClass =
  'w-full rounded-md border border-input bg-card px-3 text-body text-foreground placeholder:text-muted-foreground ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-status-failed'

function useFieldProps(id?: string) {
  const ctx = useContext(FieldContext)
  return {
    id: id ?? ctx?.id,
    'aria-describedby': ctx?.describedBy,
    'aria-invalid': ctx?.invalid || undefined,
  }
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, id, ...rest },
  ref,
) {
  return <input ref={ref} {...useFieldProps(id)} className={cn(controlClass, 'h-9', className)} {...rest} />
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, id, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} {...useFieldProps(id)} className={cn(controlClass, 'h-9 pr-8', className)} {...rest}>
      {children}
    </select>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, id, rows = 4, ...rest }, ref) {
    return (
      <textarea ref={ref} rows={rows} {...useFieldProps(id)} className={cn(controlClass, 'py-2', className)} {...rest} />
    )
  },
)
